import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepContent from '@mui/material/StepContent'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { useLocale } from '../../../i18n/useLocale'

export default function QuickStart() {
  const { urls, phone, examples, support, locale, payment, brand } = useLocale()

  const steps = [
    {
      label: 'Get your API credentials',
      content: `Contact the ${brand.name} partner team to receive your:\n\n• **API Key** - For authenticating requests\n• **Sandbox URL** - For testing`,
    },
    {
      label: 'Create a parcel',
      content: 'Make your first API call to create a parcel.',
    },
    {
      label: 'Generate a label',
      content: 'Download the shipping label for your parcel.',
    },
    {
      label: 'Track the parcel',
      content: 'Query the status of your parcel.',
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Quick Start Guide"
        description="Create your first parcel, generate a label, and track it - all in under 5 minutes."
        breadcrumbs={[
          { label: 'Getting Started', path: '/getting-started' },
          { label: 'Quick Start' },
        ]}
        badge="5 min"
        badgeColor="success"
      />

      <Alert type="info" title="Prerequisites">
        Before starting, make sure you have received your API credentials from the {brand.name} partner team.
        Contact <strong>{support.salesEmail}</strong> if you haven't been onboarded yet.
      </Alert>

      <Section title="End-to-End Flow" divider={false}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          This guide walks you through the complete flow: creating a parcel, generating a label,
          and tracking its status.
        </Typography>

        <Stepper orientation="vertical">
          {steps.map((step) => (
            <Step key={step.label} active={true}>
              <StepLabel>
                <Typography variant="h6">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {step.content}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Section>

      <Section title="Step 1: Create a Parcel">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Create a new parcel by sending a POST request to the Parcel API:
        </Typography>

        <CodeBlock
          title="Create Parcel Request"
          examples={[
            {
              language: 'bash',
              label: 'cURL',
              code: `curl -X POST "${urls.apiBase}/parcel/b2c" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '[{
    "receiverName": "${examples.personName.full}",
    "receiverEmail": "john@example.com",
    "receiverPhone": "${phone.example}",
    "destination": "${locale.countryCode.toLowerCase()}0001",
    "size": "M",
    "referenceCode": "ORDER-12345",
    "codAmount": 500.00
  }]'`,
            },
            {
              language: 'javascript',
              label: 'Node.js',
              code: `const response = await fetch('${urls.apiBase}/parcel/b2c', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': 'your-api-key',
  },
  body: JSON.stringify([{
    receiverName: '${examples.personName.full}',
    receiverEmail: 'john@example.com',
    receiverPhone: '${phone.example}',
    destination: '${locale.countryCode.toLowerCase()}0001',  // APM ID
    size: 'M',              // S, M, or L
    referenceCode: 'ORDER-12345',
    codAmount: 500.00       // Cash on Delivery (optional)
  }])
});

const result = await response.json();
console.log('Parcel Number:', result.successfulParcels[0].parcelNumber);`,
            },
          ]}
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Response</Typography>
        <SimpleCodeBlock
          code={`{
  "requestedParcelCount": 1,
  "successfulParcelCount": 1,
  "successfulParcels": [
    {
      "parcelNumber": "Q17563175099981697",
      "destination": "${locale.countryCode.toLowerCase()}0001",
      "receiverEmail": "john@example.com",
      "referenceCode": "ORDER-12345",
      "partnerParcelNumber": null,
      "errors": []
    }
  ],
  "failedParcels": []
}`}
        />

        <Alert type="success">
          Save the <InlineCode>parcelNumber</InlineCode> from the response - you'll need it to generate
          labels and track the parcel.
        </Alert>
      </Section>

      <Section title="Step 2: Generate a Label">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Generate a shipping label using the parcel number from Step 1:
        </Typography>

        <CodeBlock
          title="Generate PDF Label"
          examples={[
            {
              language: 'bash',
              label: 'cURL',
              code: `curl -X POST "${urls.apiBase}/labels/pdf" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '["Q17563175099981697"]' \\
  --output label.pdf`,
            },
            {
              language: 'javascript',
              label: 'Node.js',
              code: `const response = await fetch('${urls.apiBase}/labels/pdf', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': 'your-api-key',
  },
  body: JSON.stringify(['Q17563175099981697'])
});

// Save as PDF file
const buffer = await response.arrayBuffer();
fs.writeFileSync('label.pdf', Buffer.from(buffer));
console.log('Label saved to label.pdf');`,
            },
          ]}
        />

        <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
          For thermal printers, you can request ZPL format instead:
        </Typography>

        <SimpleCodeBlock
          code={`POST /labels/zpl
Content-Type: application/json
X-API-KEY: your-api-key

["Q17563175099981697"]`}
        />

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Label Formats</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip label="PDF" color="primary" variant="outlined" />
              <Chip label="ZPL (203 DPI)" color="primary" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Labels are A6 size (105mm x 148mm) with barcode and QR code.
            </Typography>
          </CardContent>
        </Card>
      </Section>

      <Section title="Step 3: Track the Parcel">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Check the current status of your parcel:
        </Typography>

        <CodeBlock
          title="Track Parcel Status"
          examples={[
            {
              language: 'bash',
              label: 'cURL',
              code: `curl -X GET "${urls.apiBase}/parcels/status?barcode=Q17563175099981697" \\
  -H "Accept: application/json"`,
            },
            {
              language: 'javascript',
              label: 'Node.js',
              code: `const response = await fetch(
  '${urls.apiBase}/parcels/status?barcode=Q17563175099981697'
);

const status = await response.json();
console.log('Current Status:', status.statuses[0].statusName);
console.log('Destination:', status.destinationApm.name);`,
            },
          ]}
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Response</Typography>
        <SimpleCodeBlock
          code={`{
  "parcelNumber": "Q17563175099981697",
  "partnerParcelNumber": "ORDER-12345",
  "parcelType": "B2C",
  "statuses": [
    {
      "createdAt": "2024-01-15 14:30:00",
      "statusName": "INSYS",
      "statusDescription": "The parcel was created in the system by the sender.",
      "statusShortDescription": "Created"
    }
  ],
  "destinationApm": {
    "name": "${examples.apmName}",
    "formattedAddress": "${examples.apmLocation}"
  }
}`}
        />
      </Section>

      <Section title="What's Next?">
        <Typography variant="body1" sx={{ mb: 2 }}>
          You've completed the basic integration flow. Here's what to explore next:
        </Typography>

        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
          <li>
            <strong>Batch Operations</strong> - Learn how to create multiple parcels at once via{' '}
            <a href="/guides/parcel-creation">file upload</a>
          </li>
          {payment.mobileMoney && (
            <li>
              <strong>Payment Integration</strong> - Set up{' '}
              <a href="/guides/payments">{payment.mobileMoney.name} payments</a> for pre-paid customers
            </li>
          )}
          <li>
            <strong>APM Finder Widget</strong> - Add our{' '}
            <a href="/widgets/apm-finder">location picker</a> to your checkout
          </li>
          <li>
            <strong>Webhooks</strong> - Receive real-time notifications when parcel status changes
          </li>
          <li>
            <strong>Sandbox Testing</strong> - Use our{' '}
            <a href="/testing/sandbox">sandbox environment</a> for testing
          </li>
        </Box>
      </Section>
    </Box>
  )
}
